import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useToast } from 'context/providers/ToastProvider';

const ModalMapeoCSV = ({
    show,
    onHide,
    onConfirm,
    datosCSV,
    mapeoColumnas,
    setMapeoColumnas,
    cuentasBancarias,
    cuentaSeleccionada,
}) => {
    const { showErrorToast } = useToast();
    
    const handleConfirmar = () => {
        // Validar que los campos obligatorios estén mapeados
        const camposObligatorios = ['fecha', 'descripcion', 'referencia'];
        const camposFaltantes = camposObligatorios.filter(campo => !mapeoColumnas[campo]);
        
        if (camposFaltantes.length > 0) {
            showErrorToast(`Debe mapear los siguientes campos obligatorios: ${camposFaltantes.join(', ')}`);
            return;
        }

        // Validar que al menos uno de los dos (cargo o abono) esté mapeado
        if (!mapeoColumnas.cargo && !mapeoColumnas.abono) {
            showErrorToast('Debe mapear al menos la columna de Cargo o la de Abono');
            return;
        }

        if (!cuentaSeleccionada) {
            showErrorToast('Debe seleccionar una cuenta bancaria antes de importar');
            return;
        }

        onConfirm();
    };

    const cuentaActual = cuentasBancarias.find(c => c.idCatCuentaBancaria == cuentaSeleccionada);

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            size="xl"
            backdrop="static"
        >
            <Modal.Header className="border-0 pb-2 pt-4 px-4 d-flex justify-content-between align-items-start">
                <div>
                    <h4 className="fw-bolder mb-1 text-800">Importar Estado de Cuenta</h4>
                    <p className="text-muted fs--1 mb-0">Asigna las columnas de tu archivo CSV para sincronizarlas con el sistema.</p>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={onHide}></button>
            </Modal.Header>
            <Modal.Body className="px-4 py-0">
                {cuentaActual && (
                    <div className="d-flex align-items-center mb-4 p-3 bg-light rounded-4 border-0">
                        <div className="bg-white rounded-circle shadow-sm d-flex justify-content-center align-items-center me-3" style={{ width: '48px', height: '48px' }}>
                            <i className="fas fa-university text-primary fs-1"></i>
                        </div>
                        <div>
                            <h6 className="mb-0 text-900 fw-bold">{cuentaActual.vNombreCuenta}</h6>
                            <div className="fs--2 text-500 mt-1">
                                Cuenta: <span className="text-700 fw-semi-bold me-2">{cuentaActual.vNumeroCuenta}</span>
                                Moneda: <Badge bg="success" className="ms-1">{cuentaActual.vMoneda || 'MXN'}</Badge>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mb-4">
                    <h6 className="fw-bold text-700 mb-3"><i className="fas fa-link me-2 text-primary"></i>Enlace de Columnas</h6>
                    <Row className="g-3">
                        {/* Campos Obligatorios Básicos */}
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semi-bold fs--1 mb-1 text-600">Fecha <span className="text-danger">*</span></Form.Label>
                                <Form.Select className="shadow-none border-300" size="sm" value={mapeoColumnas.fecha} onChange={(e) => setMapeoColumnas({...mapeoColumnas, fecha: e.target.value})}>
                                    <option value="">-- Seleccione --</option>
                                    {datosCSV.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semi-bold fs--1 mb-1 text-600">Descripción <span className="text-danger">*</span></Form.Label>
                                <Form.Select className="shadow-none border-300" size="sm" value={mapeoColumnas.descripcion} onChange={(e) => setMapeoColumnas({...mapeoColumnas, descripcion: e.target.value})}>
                                    <option value="">-- Seleccione --</option>
                                    {datosCSV.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semi-bold fs--1 mb-1 text-600">Referencia <span className="text-danger">*</span></Form.Label>
                                <Form.Select className="shadow-none border-300" size="sm" value={mapeoColumnas.referencia} onChange={(e) => setMapeoColumnas({...mapeoColumnas, referencia: e.target.value})}>
                                    <option value="">-- Seleccione --</option>
                                    {datosCSV.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        {/* Cargos y Abonos */}
                        <Col md={6} className="mt-4">
                            <Form.Group className="p-3 bg-light rounded-3">
                                <Form.Label className="fw-bold fs--1 mb-1 text-danger"><i className="fas fa-arrow-down me-2"></i>Cargo (Egresos)</Form.Label>
                                <Form.Select className="shadow-none border-0" size="sm" value={mapeoColumnas.cargo} onChange={(e) => setMapeoColumnas({...mapeoColumnas, cargo: e.target.value})}>
                                    <option value="">-- Opcional (si hay abono) --</option>
                                    {datosCSV.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mt-4">
                            <Form.Group className="p-3 bg-light rounded-3">
                                <Form.Label className="fw-bold fs--1 mb-1 text-success"><i className="fas fa-arrow-up me-2"></i>Abono (Ingresos)</Form.Label>
                                <Form.Select className="shadow-none border-0" size="sm" value={mapeoColumnas.abono} onChange={(e) => setMapeoColumnas({...mapeoColumnas, abono: e.target.value})}>
                                    <option value="">-- Opcional (si hay cargo) --</option>
                                    {datosCSV.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </div>

                <Card className="border-0 shadow-none bg-light">
                    <Card.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                            <span className="fw-semibold fs--1 text-600"><i className="fas fa-eye me-2"></i>Vista Previa (5 filas)</span>
                            <span className="fs--2 text-muted">{datosCSV.rows.length + 1} registros totales</span>
                        </div>
                        <div className="rounded-3 border border-200 bg-white" style={{ maxHeight: '180px', overflowX: 'auto', overflowY: 'auto' }}>
                            <Table hover size="sm" className="mb-0 fs--2" style={{ whiteSpace: 'nowrap' }}>
                                <thead className="bg-200 sticky-top text-uppercase">
                                    <tr>
                                        {datosCSV.headers.map((header, idx) => (
                                            <th key={idx} className="fw-medium text-secondary py-2">
                                                {header} <span className="text-400 ms-1 fw-normal">(Col {idx})</span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {datosCSV.rows.map((row, rowIdx) => (
                                        <tr key={rowIdx}>
                                            {row.map((cell, cellIdx) => (
                                                <td key={cellIdx} className="text-700">{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            </Modal.Body>
            <Modal.Footer className="border-0 bg-transparent px-4 pb-4 pt-0">
                <Button variant="light" onClick={onHide} className="rounded-pill px-4 fw-semi-bold">
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleConfirmar} className="rounded-pill px-4 fw-semi-bold shadow-sm">
                    <i className="fas fa-check me-2"></i> Confirmar Importación
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalMapeoCSV.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    datosCSV: PropTypes.shape({
        headers: PropTypes.arrayOf(PropTypes.string),
        rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string))
    }),
    mapeoColumnas: PropTypes.shape({
        fecha: PropTypes.string,
        descripcion: PropTypes.string,
        cargo: PropTypes.string,
        abono: PropTypes.string,
        referencia: PropTypes.string
    }),
    setMapeoColumnas: PropTypes.func.isRequired,
    cuentasBancarias: PropTypes.arrayOf(
        PropTypes.shape({
            idCatCuentaBancaria: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            vNombreCuenta: PropTypes.string,
            vNumeroCuenta: PropTypes.string,
            vBanco: PropTypes.string,
            vMoneda: PropTypes.string,
            vTipoCuenta: PropTypes.string
        })
    ),
    cuentaSeleccionada: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setCuentaSeleccionada: PropTypes.func,
    tipoMovimientoDefault: PropTypes.string,
    setTipoMovimientoDefault: PropTypes.func
};

export default ModalMapeoCSV;
